import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT, getEnglish } from '../utils/i18n';
import { JsonLd } from '../utils/seo';
interface FaqItem {
  questionKey: string;
  answer: string | React.ReactNode;
  answerKey?: string;
  /**
   * Plain-text answer used ONLY for the FAQPage structured data. Required on
   * items whose visible answer is JSX (schema can't carry React nodes).
   * Items using `answerKey` get their schema text from the translation.
   */
  seoAnswer?: string;
}
// Ordered by what a customer needs before they can order: where we go, what it
// costs, how fast, how much. Product-choice questions follow (they're the ones
// that pull search traffic), then logistics, then policy.
const faqs: FaqItem[] = [{
  questionKey: 'faq.q1',
  seoAnswer: 'We deliver throughout King and Pierce counties — Bellevue, Kirkland, Redmond, Issaquah, Renton, Seattle, Auburn, Kent, Federal Way, Tacoma, Puyallup, Bonney Lake, and beyond.',
  answer: <>
        We deliver throughout King and Pierce counties — Bellevue, Kirkland,
        Redmond, Issaquah, Renton, Seattle, Auburn, Kent, Federal Way, Tacoma,
        Puyallup, Bonney Lake, and beyond.{' '}
        <Link to="/delivery-zones" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-757" data-mp="1fo42r0tr9npx">
          View our full delivery zone map →
        </Link>
      </>
}, {
  questionKey: 'faq.q12',
  answerKey: 'faq.a12',
  answer: ''
}, {
  questionKey: 'faq.q3',
  answerKey: 'faq.a3',
  answer: ''
}, {
  questionKey: 'faq.q2',
  answerKey: 'faq.a2',
  answer: ''
}, {
  questionKey: 'faq.q4',
  seoAnswer: 'Use our Material Calculator & Quiz to estimate based on your project dimensions, or compare materials side by side. You can also call us at (206) 619-3742 and we will help you figure it out.',
  answer: <>
        Use our{' '}
        <Link to="/help" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-758" data-mp="n22kcrkycpo9">
          Material Calculator & Quiz
        </Link>{' '}
        to estimate based on your project dimensions, or{' '}
        <Link to="/compare" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-759" data-mp="2fwtut002xoy0">
          compare materials
        </Link>{' '}
        side by side. You can also give us a call and we'll help you figure it
        out.
      </>
}, {
  questionKey: 'faq.q8',
  answerKey: 'faq.a8',
  answer: ''
}, {
  questionKey: 'faq.q9',
  answerKey: 'faq.a9',
  answer: ''
}, {
  questionKey: 'faq.q10',
  answerKey: 'faq.a10',
  answer: ''
}, {
  questionKey: 'faq.q11',
  answerKey: 'faq.a11',
  answer: ''
}, {
  questionKey: 'faq.q13',
  answerKey: 'faq.a13',
  answer: ''
}, {
  questionKey: 'faq.q14',
  answerKey: 'faq.a14',
  answer: ''
}, {
  questionKey: 'faq.q15',
  answerKey: 'faq.a15',
  answer: ''
}, {
  questionKey: 'faq.q5',
  answerKey: 'faq.a5',
  answer: ''
}, {
  questionKey: 'faq.q16',
  answerKey: 'faq.a16',
  answer: ''
}, {
  questionKey: 'faq.q6',
  answerKey: 'faq.a6',
  answer: ''
}, {
  questionKey: 'faq.q7',
  seoAnswer: 'It depends on the situation. In some cases we can spread the material depending on access, terrain, and the type of project. Call us at (206) 619-3742 to discuss your specific setup, and see our Delivery Prep Guide to get your site ready.',
  answer: <>
        It depends on the situation. In some cases we can spread the material
        depending on access, terrain, and the type of project.{' '}
        <a href="tel:206-619-3742" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-760" data-mp="mg7l15dpx13v">
          Give us a call
        </a>{' '}
        to discuss your specific setup. Check out our{' '}
        <Link to="/delivery-prep" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-761" data-mp="2s72ydqqblrlv">
          Delivery Prep Guide
        </Link>{' '}
        to get your site ready.
      </>
}];
export function FAQ() {
  const t = useT();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [lastClosedIndex, setLastClosedIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    if (openIndex === index) {
      // Closing this question — mark it as last closed
      setLastClosedIndex(index);
      setOpenIndex(null);
    } else {
      // Opening a new question — clear last closed
      setLastClosedIndex(null);
      setOpenIndex(index);
    }
  };
  return <section className="py-12 sm:py-16 dark:bg-[#202020] transition-colors duration-300 scroll-mt-32 bg-[#F2EFE8]" id="faq" data-id="element-762" data-mp="1e6x3ny9y68wx">
      {/* FAQPage markup — these questions match real search queries, so they
          were invisible to Google without it. English text only: the schema
          describes the canonical page. */}
      <JsonLd data={{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(f => ({
        '@type': 'Question',
        name: getEnglish(f.questionKey),
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.seoAnswer ?? (f.answerKey ? getEnglish(f.answerKey) : '')
        }
      }))
    }} data-id="element-763" data-mp="10dpd89xlrjgt" />
      {/* Header */}
      <div className="mx-auto mb-8 max-w-5xl px-4 sm:mb-12 sm:px-6" data-id="element-764" data-mp="2x5l82pr3qpeg">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} data-id="element-765" data-mp="2x38j3smxferw">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-baseline md:gap-7" data-id="element-766" data-mp="3prxpo76r4cb1">
            <h2 className="font-heading text-5xl font-extrabold leading-[1.1] tracking-tight text-stone-900 dark:text-white md:text-6xl lg:text-7xl" data-id="element-767" data-mp="3uqhau5ditk2c">
              Da <span className="font-black text-[#04A05C]" data-id="element-768" data-mp="3ktment4pzvr8">FAQs</span>
            </h2>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#17A348]" data-id="element-769" data-mp="25dnkxmvuo2ai">— Get answers fast</span>
          </div>
          <p className="max-w-2xl font-sub text-lg font-medium leading-relaxed tracking-wide text-brand-charcoal dark:text-white" data-id="element-770" data-mp="29h22wj3cmsql">
            {t('faq.subtitle')}
          </p>
        </motion.div>
      </div>

      {/* FAQ List - Full Width */}
      <div className="w-full" data-id="element-771" data-mp="66yn8m3kszcf">
        {faqs.map((faq, index) => {
        const isOpen = openIndex === index;
        const answerContent = faq.answerKey ? t(faq.answerKey) : faq.answer;
        return <motion.div key={index} initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05,
          duration: 0.3
        }} className={`w-full transition-colors duration-300 ${isOpen ? 'bg-[#00754A]' : 'bg-transparent'}`} data-id="element-772" data-mp="16168cuaybci" data-mpi={index}>
              <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-773" data-mp="3ef72sdf8ylap">
                {/* Row padding sets the height. A fixed pixel height was
                    clipping/misaligning the longer questions, since they wrap
                    to two or three lines on narrow screens. */}
                <button onClick={() => toggleFAQ(index)} className="w-full flex items-start justify-between gap-5 py-6 sm:py-8 text-left group" aria-expanded={isOpen} aria-controls={`faq-answer-${index}`} data-id="element-774" data-mp="1l4b0v4q4eok6">
                  <span className={`font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold leading-[1.15] transition-colors duration-300 ${isOpen ? 'text-white' : lastClosedIndex === index ? 'text-[#04A05C] dark:text-[#04A05C] group-hover:text-[#048036] dark:group-hover:text-[#048036]' : 'text-stone-900 dark:text-white group-hover:text-[#04A05C] dark:group-hover:text-[#04A05C]'}`} data-id="element-775" data-mp="1tvuwhhzgoiax">
                    {t(faq.questionKey)}
                  </span>
                  <motion.span animate={{
                rotate: isOpen ? 45 : 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className={`shrink-0 mt-0.5 sm:mt-1 transition-colors duration-300 ${isOpen ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-776" data-mp="1b7yb2hxp4uup">
                    <Plus className="w-7 h-7 sm:w-9 sm:h-9 stroke-[1.25]" data-id="element-777" data-mp="2rj26flq6uxkj" />
                  </motion.span>
                </button>

                <AnimatePresence data-id="element-778" data-mp="3cm32c14bbqew">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" id={`faq-answer-${index}`} data-id="element-779" data-mp="38n3y6iqenxoi">
                      <p className="pb-7 sm:pb-9 text-base sm:text-lg md:text-xl text-white/95 leading-relaxed max-w-3xl font-medium" data-id="element-780" data-mp="3rdv9hptydy5j">
                        {answerContent}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Still Have Questions */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-8 sm:mt-10" data-id="element-781" data-mp="2b0n37k559w5x">
        <div className="flex flex-col sm:flex-row items-center justify-center text-center sm:text-left py-4 gap-3 sm:gap-5" data-id="element-782" data-mp="261jzc30whvzj">
          <MessageCircle className="w-5 h-5 text-[#04A05C] shrink-0" data-id="element-783" data-mp="lk136wuxsvci" />
          <p className="text-stone-900 dark:text-white font-heading font-extrabold text-lg sm:text-xl" data-id="element-784" data-mp="qg9jweos8wls">
            {t('faq.stillQuestions')}
            <span className="font-normal text-stone-600 dark:text-stone-400 text-base ml-2" data-id="element-785" data-mp="gqq3dfojleu3">
              {t('faq.shootText')}
            </span>
          </p>
          <a href="sms:206-619-3742" className="inline-flex items-center justify-center px-6 py-2.5 hover:bg-[#048B3A] text-white rounded-full font-bold text-sm transition-colors bg-[#04A05C]" data-id="element-786" data-mp="2pvu6uto79ud">
            {t('faq.textUs')}
          </a>
        </div>
      </div>
    </section>;
}